import {
  DisclosureCollectionProps,
  DisclosureGroupingsForCollectionProps,
  DisclosureWithTitleProps,
} from 'libs/contentful/props';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import DisclosureCollectionTableOfContents from './DisclosureCollectionTableOfContents';

/**
 * Generates a URL-safe anchor ID from a title
 */
const generateAnchorId = (title: string, index: number): string => {
  const slug =
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || 'grouping';
  return `grouping-${index + 1}-${slug}`;
};

/**
 * Checks if a grouping has any valid disclosure content
 */
const hasValidContent = (grouping: DisclosureGroupingsForCollectionProps): boolean => {
  const disclosures = grouping.disclosures?.items ?? [];
  return disclosures.some(disclosure => disclosure?.copy?.json || disclosure?.disclosureItem?.copy?.json);
};

/**
 * DisclosureCollection renders multiple DisclosureGroupings with a shared
 * table of contents. Each grouping has a title that becomes a TOC entry
 * and heading, containing disclosure items with inline bold titles.
 */
const DisclosureCollection = ({ groupingsCollection, tableOfContentsExpanded }: DisclosureCollectionProps) => {
  const groupings = (groupingsCollection?.items ?? []).filter(Boolean);

  // Filter groupings to only include those with valid title and content
  const groupingsWithContent = groupings.filter(
    (grouping): grouping is DisclosureGroupingsForCollectionProps => !!grouping.title && hasValidContent(grouping),
  );

  if (groupingsWithContent.length === 0) {
    return null;
  }

  // Generate TOC items from grouping titles
  const tocItems = groupingsWithContent.map((grouping, index) => ({
    label: grouping.title ?? '',
    anchor: generateAnchorId(grouping.title ?? `grouping-${index}`, index),
  }));

  return (
    <section className="content-wrapper py-4">
      <div className="max-w-[765px]">
        {/* Table of Contents - Client Component for interactivity */}
        {tocItems.length > 0 && (
          <DisclosureCollectionTableOfContents items={tocItems} defaultExpanded={tableOfContentsExpanded ?? true} />
        )}

        {/* Disclosure Groupings */}
        <div className="space-y-12">
          {groupingsWithContent.map((grouping, index) => (
            <DisclosureGroupingItem
              key={grouping.sys?.id ?? index}
              anchorId={generateAnchorId(grouping.title ?? `grouping-${index}`, index)}
              index={index}
              {...grouping}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

interface DisclosureGroupingItemProps extends DisclosureGroupingsForCollectionProps {
  anchorId: string;
  index: number;
}

/**
 * Renders a single DisclosureGrouping within the collection.
 * The grouping title becomes a numbered heading, and each disclosure's title
 * is rendered inline (bolded) before the copy.
 */
const DisclosureGroupingItem = ({
  anchorId,
  index,
  title,
  disclosures: disclosuresData,
}: DisclosureGroupingItemProps) => {
  const disclosures = (disclosuresData?.items ?? []).filter(Boolean);

  // Filter disclosures to only include those with valid copy content
  const disclosuresWithContent = disclosures.filter(
    disclosure => disclosure.copy?.json || disclosure.disclosureItem?.copy?.json,
  );

  if (disclosuresWithContent.length === 0) {
    return null;
  }

  return (
    <section id={anchorId} className="scroll-mt-24">
      {/* Grouping title as numbered heading */}
      <h2 className="flex font-sans text-heading-sm md:text-heading-md font-semibold mb-6">
        <span className="shrink-0 w-8 md:w-10">{index + 1}.</span>
        <span>{title}</span>
      </h2>

      {/* Disclosure items with inline bold titles */}
      <div className="space-y-6">
        {disclosuresWithContent.map((disclosure, disclosureIndex) => (
          <DisclosureItem key={disclosure.sys?.id ?? disclosureIndex} {...disclosure} />
        ))}
      </div>
    </section>
  );
};

/**
 * Renders a single disclosure with its title inline (bolded) before the copy.
 */
const DisclosureItem = ({ title, copy, disclosureItem }: DisclosureWithTitleProps) => {
  // Use copy field if available, otherwise fall back to legacy disclosureItem
  const copyContent = copy || disclosureItem?.copy;

  if (!copyContent?.json) {
    return null;
  }

  return (
    <div className="font-serif text-body-serif">
      {title && <span className="font-sans font-bold">{title} </span>}
      <RichTextCopy copy={copyContent} />
    </div>
  );
};

export default DisclosureCollection;
